// Background queue worker: replays pending plant identifications when Gemini
// was slow (504) or unreachable during the initial capture. Each queued item is
// a draft scan_id — we drive it forward with plants.identifyExisting(scan_id).
//
// Trigger points wired up in <App /> via useIdentifyQueueWorker():
//   • on user login / app mount
//   • on window.online event
//   • every FLUSH_INTERVAL_MS (default 90s)

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import i18n from "../i18n";
import { plants as plantsApi } from "./api";
import {
  listIdentifyQueue,
  dequeueIdentifyRetry,
  bumpIdentifyRetry,
} from "./offlineCache";

const FLUSH_INTERVAL_MS = 90_000;   // 90s background poll
const MAX_RETRIES = 5;              // after this, we give up (user still sees the draft)
const BACKOFF_BASE_MS = 30_000;     // 30s * 2^retries capped
const BACKOFF_CAP_MS = 10 * 60_000; // 10 min max

const isTransient = (err) => {
  if (!err) return false;
  if (!err.response) return true;
  const s = err.response.status;
  return s === 408 || s === 429 || s === 502 || s === 503 || s === 504 || s === 524;
};

let _flushing = false;

export const flushIdentifyQueue = async (userId) => {
  if (_flushing) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  _flushing = true;
  try {
    const entries = (await listIdentifyQueue(userId))
      .filter((e) => (e.next_attempt_at || 0) <= Date.now())
      .sort((a, b) => (a.added_at || 0) - (b.added_at || 0));

    for (const entry of entries) {
      try {
        const { data } = await plantsApi.identifyExisting(entry.scan_id);
        await dequeueIdentifyRetry(entry.scan_id);
        const name = data?.identification?.common_name || "";
        const lower = name.trim().toLowerCase();
        if (name && lower !== "unknown" && lower !== "desconocido") {
          toast.success(i18n.t("queue.identifiedSuccess", { name }));
        }
      } catch (err) {
        if (isTransient(err) && (entry.retries || 0) + 1 < MAX_RETRIES) {
          const backoff = Math.min(
            BACKOFF_BASE_MS * Math.pow(2, entry.retries || 0),
            BACKOFF_CAP_MS,
          );
          await bumpIdentifyRetry(
            entry.scan_id,
            err?.response?.data?.detail || err?.message || "transient",
            backoff,
          );
        } else {
          // Give up — remove from queue. The draft remains visible in My Plants
          // so the user can still tap "Identify now" manually.
          await dequeueIdentifyRetry(entry.scan_id);
          toast.message(i18n.t("queue.gaveUp"), {
            description: i18n.t("queue.gaveUpDesc"),
            duration: 8000,
          });
        }
      }
    }
  } finally {
    _flushing = false;
  }
};

/**
 * Mount once at the top level of the authenticated app. Kicks the queue on
 * mount, on window.online, and on a fixed interval. `userId` may be null while
 * auth is loading — the effect will re-run once it's set.
 */
export const useIdentifyQueueWorker = (userId) => {
  const timerRef = useRef(null);
  useEffect(() => {
    if (!userId) return undefined;
    // Kick immediately (small delay so we don't race with initial gallery fetch).
    const initial = setTimeout(() => { flushIdentifyQueue(userId); }, 4_000);

    const onOnline = () => flushIdentifyQueue(userId);
    window.addEventListener("online", onOnline);

    timerRef.current = window.setInterval(() => flushIdentifyQueue(userId), FLUSH_INTERVAL_MS);

    return () => {
      clearTimeout(initial);
      if (timerRef.current) window.clearInterval(timerRef.current);
      window.removeEventListener("online", onOnline);
    };
  }, [userId]);
};
